import { useMemo } from "react";
import { Box, Button, Tooltip } from "@chakra-ui/react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { format, formatDistanceToNow } from "date-fns";
import GenericTable from "components/Table/GenericTable";
import { CDS } from "design-system";
import { fetchGroups } from "../api";
import { groupKeys } from "../hooks";
import type { Group, GroupsFilters } from "../types";
import GroupTypeTag from "./GroupTypeTag";

const columnHelper = createColumnHelper<Group>();

interface GroupsTableProps {
  filters: GroupsFilters;
  searchValue: string;
  // Format: "<field>_<direction>" e.g. "name_asc", "scim_last_synced_at_desc"
  currentSort: string;
  onSelectGroup: (group: Group) => void;
}

function splitSort(value: string): { sortBy: string; sortDirection: "asc" | "desc" } {
  const lastUnderscore = value.lastIndexOf("_");
  if (lastUnderscore === -1) return { sortBy: value, sortDirection: "asc" };
  const direction = value.slice(lastUnderscore + 1);
  return {
    sortBy: value.slice(0, lastUnderscore),
    sortDirection: direction === "desc" ? "desc" : "asc",
  };
}

export default function GroupsTable({ filters, searchValue, currentSort, onSelectGroup }: GroupsTableProps) {
  const additionalQueries = useMemo(() => {
    const { sortBy, sortDirection } = splitSort(currentSort);
    return {
      group_type: filters.groupType || undefined,
      q: searchValue || undefined,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };
  }, [filters.groupType, searchValue, currentSort]);

  const columns = useMemo<ColumnDef<Group, any>[]>(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        enableSorting: false,
        cell: ({ row, getValue }) => (
          <Button variant="unstyled" textAlign="left" onClick={() => onSelectGroup(row.original)}>
            <CDS.Text14 fontWeight="medium" wordBreak="break-word">{getValue()}</CDS.Text14>
          </Button>
        ),
      }),
      columnHelper.accessor("groupType", {
        header: "Type",
        enableSorting: false,
        cell: ({ getValue }) => <GroupTypeTag groupType={getValue()} />,
      }),
      columnHelper.accessor("memberCount", {
        header: "Members",
        enableSorting: false,
        cell: ({ getValue }) => <CDS.Text14>{getValue()}</CDS.Text14>,
      }),
      columnHelper.accessor("scimProvisioned", {
        header: "Source",
        enableSorting: false,
        cell: ({ getValue }) => (
          getValue() ? (
            <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.primary}>SCIM</CDS.Tag>
          ) : (
            <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>Manual</CDS.Tag>
          )
        ),
      }),
      columnHelper.accessor("scimLastSyncedAt", {
        header: "Last synced",
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <CDS.Text14 color="fg.subtle">—</CDS.Text14>;
          const parsed = new Date(value);
          return (
            <Tooltip label={format(parsed, "MMM d, yyyy 'at' h:mm a")} placement="top" hasArrow>
              <Box as="span" cursor="default">
                <CDS.Text14>{formatDistanceToNow(parsed, { addSuffix: true })}</CDS.Text14>
              </Box>
            </Tooltip>
          );
        },
      }),
    ],
    [onSelectGroup],
  );

  return (
    <GenericTable<Group>
      fetchData={fetchGroups}
      queryKey={(queryString) => groupKeys.list(queryString)}
      columns={columns}
      meta={{ pagination: { page: 0, perPage: 50 } }}
      additionalQueries={additionalQueries}
      pageSizeOptions={[25, 50, 100]}
      textWrap
    />
  );
}
